import { CommonDebugger } from '../../../entities';
import type { VAxios } from '../../define/axios/Axios';
import { PageWrapper } from '../../entities/base/base.entities';
import { IBatchFile, IBatchTag, IBatchTagGroup } from '../../entities/cc/batch.entities';
import { chunk } from 'lodash-es';

/**
 * 内容中心接口配置
 */
export const createApi = (defHttp: VAxios) => ({
  // 废弃文件
  discard: (
    id: string,
    params: {
      files?: {
        fileIds?: string[];
        jobId?: string;
      }[];
    }
  ) => {
    const fileChunks = chunk(params.files, 20);
    // 将废弃改为串行执行
    return fileChunks.reduce(
      (promise, chunk) =>
        promise.then(() =>
          defHttp.post({
            url: `/aiad/batch/${id}/files/discard`,
            params: {
              files: chunk,
            },
          })
        ),
      Promise.resolve()
    );
  },

  // 恢复废弃文件
  restore: (
    id: string,
    params: {
      files?: {
        fileIds?: string[];
        jobId?: string;
      }[];
    }
  ) => {
    const fileChunks = chunk(params.files, 20);
    // 将恢复改为串行执行
    return fileChunks.reduce(
      (prev, chunk) =>
        prev.then(() =>
          defHttp.post({
            url: `/aiad/batch/${id}/files/restore`,
            params: {
              files: chunk,
            },
          })
        ),
      Promise.resolve()
    );
  },

  getWorkflows: (params: {
    ability?: string;
    batchNo?: string;
    endTime?: string;
    page?: number;
    pageSize?: number;
    sortField?: string;
    sortOrder?: string;
    startTime?: string;
    status?: string;
    userId?: string;
    remark?: string;
  }) => {
    return defHttp.get({
      url: `/aiad/batch/workflow/creations`,
      params,
    });
  },

  getBatch: (params: {
    ability?: string;
    endTime?: string;
    page?: number;
    pageSize?: number;
    sortField?: string;
    sortOrder?: string;
    startTime?: string;
    status?: string;
    batchNo?: string;
    userId?: string;
    source?: string;
    remark?: string;
  }) => {
    return defHttp.get({
      url: `/aiad/batch`,
      params,
    });
  },

  getBatchJobs: (
    id,
    params: {
      page: number;
      pageSize: number;
      sortField?: string;
      sortOrder?: string;
      status?: string;
    }
  ) => {
    return defHttp.get({
      url: `/aiad/batch/${id}/jobs`,
      params,
    });
  },

  // 修改结果文件
  updateBatchJobResult: (id, params) => {
    return defHttp.put({
      url: `/aiad/batch/jobs/${id}`,
      params,
    });
  },

  addBatchTextTask: (params: {
    ability: string;
    ar: number;
    arString: string;
    file: string;
    styleCode?: string;
    subStyleCode?: string;
    num?: number;
  }) => {
    return defHttp.post({
      url: `/aiad/batch/txt2img`,
      params,
    });
  },

  addBatchImageTask: (params: any) => {
    return defHttp.post({
      url: `/aiad/batch/reimage`,
      params,
    });
  },

  addBatchDetectTask: (params: {
    urls?: string[];
    images: any[];
    extendTag: boolean;
    sourceBatchId?: string;
  }) => {
    return defHttp.post({
      url: `/aiad/batch/detect`,
      params,
    });
  },

  addBatchTortTask: (params: {
    urls?: string[];
    images: any[];
    extendTag: boolean;
    sourceBatchId?: string;
  }) => {
    return defHttp.post({
      url: `/aiad/batch/tort`,
      params,
    });
  },

  addBatchImage2PrintTask: (params: {
    urls?: string[];
    images: any[];
    extendTag: boolean;
    sourceBatchId?: string;
  }) => {
    return defHttp.post({
      url: `/aiad/batch/image_2_print`,
      params,
    });
  },

  addBatchTKVideoTask: (params: {
    urls?: string[];
    images: any[];
    extendTag: boolean;
    sourceBatchId?: string;
  }) => {
    return defHttp.post({
      url: `/aiad/batch/tk_video`,
      params,
    });
  },

  addBatchTileTask: (params) => {
    return defHttp.post({
      url: `/aiad/batch/tile`,
      params,
    });
  },

  addBatchBlendTask: (params) => {
    return defHttp.post({
      url: `/aiad/batch/blend`,
      params,
    });
  },

  addBatchMultiSizeTask: (params) => {
    return defHttp.post({
      url: `/aiad/batch/multi-size`,
      params,
    });
  },

  addBatchPolyptychTask: (params) => {
    return defHttp.post({
      url: `/aiad/batch/polyptych`,
      params,
    });
  },

  addBatchStyleReferenceTask: (params) => {
    return defHttp.post({
      url: `/aiad/batch/style-reference`,
      params,
    });
  },

  addBatchMockupTask: (params: {
    images: any[];
    urls?: string[];
    templates: string[];
    random?: boolean;
    extendTag: boolean;
    sourceBatchId?: string;
  }) => {
    return defHttp.post({
      url: `/aiad/batch/mockup`,
      params,
    });
  },

  addBatchDressTask: (params) => {
    return defHttp.post({
      url: `/aiad/batch/dress`,
      params,
    });
  },

  addBatchPrintingTask: (params) => {
    return defHttp.post({
      url: `/aiad/batch/print`,
      params,
    });
  },

  addDIYCompositingTask: (params) => {
    return defHttp.post({
      url: `/aiad/batch/diy-compositing`,
      params,
    });
  },

  addDIYFulfillmentTask: (params) => {
    return defHttp.post({
      url: `/aiad/batch/diy-fulfillment`,
      params,
    });
  },

  addBatchMattingTask: (params) => {
    return defHttp.post({
      url: `/aiad/batch/matting`,
      params,
    });
  },

  addCutPiecesTask: (params) => {
    return defHttp.post({
      url: `/aiad/batch/cuts`,
      params,
    });
  },

  retryTask: (id) => {
    return defHttp.put({
      url: `/aiad/batch/jobs/retry/${id}`,
    });
  },

  getBatchDetail: (id) => {
    return defHttp.get({
      url: `/aiad/batch/${id}`,
    });
  },

  getBatchDetailList: (params: {
    id: string | number;
    page?: number;
    pageSize?: number;
    sortField?: string;
    sortOrder?: string;
    status?: string | number;
    ids?: string;
    discard?: null | boolean;
  }) => {
    return defHttp.get({
      url: `/aiad/batch/${params.id}/jobs`,
      params,
    });
  },

  getAllFiles: (id) => {
    return defHttp.get<
      {
        id: string;
        debugger: CommonDebugger;
        results: {
          url: string;
          name: string;
          text: string;
        }[];
      }[]
    >({
      url: `/aiad/batch/${id}/files`,
    });
  },

  getMockupTemplateById: (id) => {
    return defHttp.get({
      url: `/aiad/templates/mockup/${id}`,
    });
  },

  getMockupTemplates: (params) => {
    return defHttp.get({
      url: `/aiad/templates/mockup`,
      params,
    });
  },

  deleteMockupTemplate: (id) => {
    return defHttp.delete({
      url: `/aiad/templates/mockup/${id}`,
    });
  },

  addMockupTemplate: (params: { design: any; name: string; preview: string }) => {
    return defHttp.post({
      url: `/aiad/templates/mockup`,
      params,
    });
  },

  // 套图模板列表
  getGroupTemplates: async (params) => {
    if (params.templateGroupId) {
      let group = params.templateGroupId;
      let recommend: boolean = null;
      const isPsd = params.templateType === 'psd';
      console.log(params);
      if (group === 'recommend') {
        group = null;
        recommend = !isPsd;
      }

      const res: any = await defHttp.get({
        url: `/aiad/batch/templates`,
        params: {
          group,
          recommend,
          type: isPsd ? 'psd' : 'mockup',
        },
      });
      return {
        ...res,
        items: res.items?.map((item) => {
          return {
            ...item.meta,
            name: item.name,
            templateGroupId: item.group,
            tempPreview: item.preview,
            templateType: isPsd ? 'psd' : 'lingtu',
            isTemplate: true,
          };
        }),
      };
    }
    return defHttp.get({
      url: '/aiad/mock/templates',
      params,
    });
  },
  // 新增套图模板
  addGroupTemplate: (params) => {
    return defHttp.post({
      url: `/aiad/mock/templates`,
      params: {
        templateType: 'lingtu',
        ...params,
      },
    });
  },

  // 修改mockup模板
  updateGroupTemplate: (id, params) => {
    return defHttp.put({ url: `/aiad/mock/templates/${id}`, params });
  },

  // 获取套图模板详情
  getGroupTemplateInfo: (id) => {
    return defHttp.get({
      url: `/aiad/mock/templates/${id}`,
    });
  },
  // 删除套图模板
  deleteGroupTemplate: (id) => {
    return defHttp.delete({
      url: `/aiad/mock/templates/${id}`,
    });
  },

  // 获取套图模板分组
  getGroupTemplateGroups: (params) => {
    return defHttp.get({
      url: '/aiad/mock/templates/groups',
      params,
    });
  },

  // 新增套图模板分组
  addGroupTemplateGroup: (params) => {
    return defHttp.post({
      url: '/aiad/mock/templates/groups',
      params,
    });
  },

  // 修改套图模板分组
  updateGroupTemplateGroup: (id, params) => {
    return defHttp.put({
      url: `/aiad/mock/templates/groups/${id}`,
      params,
    });
  },

  // 删除套图模板分组
  deleteGroupTemplateGroup: (id) => {
    return defHttp.delete({
      url: `/aiad/mock/templates/groups/${id}`,
    });
  },

  // 印花模板列表
  getPrintingTemplates: async (params) => {
    if (params.templateGroupId) {
      const res: any = await defHttp.get({
        url: `/aiad/batch/templates`,
        params: {
          group: params.templateGroupId,
          type: 'print',
        },
      });
      return {
        ...res,
        items: res.items?.map((item) => {
          return {
            ...item.meta,
            name: item.name,
            templateGroupId: item.group,
            isTemplate: true,
          };
        }),
      };
    }
    return defHttp.get({
      url: '/aiad/print/templates',
      params,
    });
  },
  // 热门印花模板
  getHotPrintingTemplates: (params) => {
    return defHttp.get({
      url: '/aiad/print/templates/hots',
      params,
    });
  },
  // 新增印花模板
  addPrintingTemplate: (params) => {
    return defHttp.post({
      url: `/aiad/print/templates`,
      params,
    });
  },

  // 修改印花模板
  updatePrintingTemplate: (id, params) => {
    return defHttp.put({ url: `/aiad/print/templates/${id}`, params });
  },

  // 获取印花模板详情
  getPrintingTemplateInfo: (id) => {
    return defHttp.get({
      url: `/aiad/print/templates/${id}`,
    });
  },
  // 删除印花模板
  deletePrintingTemplate: (id) => {
    return defHttp.delete({
      url: `/aiad/print/templates/${id}`,
    });
  },
  // 获取印花模板分组
  getPrintingTemplateGroups: (params) => {
    return defHttp.get({
      url: '/aiad/print/templates/groups',
      params,
    });
  },

  // 新增印花模板分组
  addPrintingTemplateGroup: (params) => {
    return defHttp.post({
      url: '/aiad/print/templates/groups',
      params,
    });
  },

  // 修改印花模板分组
  updatePrintingTemplateGroup: (id, params) => {
    return defHttp.put({
      url: `/aiad/print/templates/groups/${id}`,
      params,
    });
  },

  // 删除印花模板分组
  deletePrintingTemplateGroup: (id) => {
    return defHttp.delete({
      url: `/aiad/print/templates/groups/${id}`,
    });
  },

  // 给印花模板设置分组
  setPrintingTemplateGroup: (params) => {
    return defHttp.put({
      url: `/aiad/print/templates/set-groups`,
      params,
    });
  },

  // 查询生产图模板
  getCutPiecesTemplate: (params) => {
    return defHttp.get({
      url: `/aiad/cutpieces/templates`,
      params,
    });
  },
  // 新增生产图模版
  addCutPiecesTemplate: (params) => {
    return defHttp.post({
      url: `/aiad/cutpieces/templates`,
      params,
    });
  },
  // 获取生产图模版信息
  getCutPiecesTemplateInfo: (id) => {
    return defHttp.get({
      url: `/aiad/cutpieces/templates/${id}`,
    });
  },
  // 更新生产图模版
  updateCutPiecesTemplate: (id, params) => {
    return defHttp.put({
      url: `/aiad/cutpieces/templates/${id}`,
      params,
    });
  },
  // 删除生产图模版
  deleteCutPiecesTemplate: (id) => {
    return defHttp.delete({
      url: `/aiad/cutpieces/templates/${id}`,
    });
  },
  // mockup模板绑定生产图模板
  bindCutPiecesTemplate: (params) => {
    return defHttp.post({
      url: `/aiad/mock/templates/${params.id}/cut`,
      params,
    });
  },

  // 来图定制模板列表
  getDIYTemplates: async (params) => {
    if (params.templateGroupId) {
      const res: any = await defHttp.get({
        url: `/aiad/batch/templates`,
        params: {
          group: params.templateGroupId === 'all' ? undefined : params.templateGroupId,
          type: 'diy',
        },
      });
      return {
        ...res,
        items: res.items?.map((item) => {
          return {
            ...item.meta,
            name: item.name,
            templateGroupId: item.group,
            isTemplate: true,
          };
        }),
      };
    }
    return defHttp.get({
      url: '/aiad/diy/templates',
      params,
    });
  },

  // 新增DIY模板
  addDIYTemplate: (params) => {
    return defHttp.post({
      url: `/aiad/diy/templates`,
      params,
    });
  },

  // 修改DIY模板
  updateDIYTemplate: (id, params) => {
    return defHttp.put({ url: `/aiad/diy/templates/${id}`, params });
  },

  // 获取DIY模板详情
  getDIYTemplateInfo: (id) => {
    return defHttp.get({
      url: `/aiad/diy/templates/${id}`,
    });
  },
  // 删除DIY模板
  deleteDIYTemplate: (id) => {
    return defHttp.delete({
      url: `/aiad/diy/templates/${id}`,
    });
  },
  // 获取DIY模板分组
  getDIYTemplateGroups: (params) => {
    return defHttp.get({
      url: '/aiad/diy/templates/groups',
      params,
    });
  },

  // 新增DIY模板分组
  addDIYTemplateGroup: (params) => {
    return defHttp.post({
      url: '/aiad/diy/templates/groups',
      params,
    });
  },

  // 修改DIY模板分组
  updateDIYTemplateGroup: (id, params) => {
    return defHttp.put({
      url: `/aiad/diy/templates/groups/${id}`,
      params,
    });
  },

  // 删除DIY模板分组
  deleteDIYTemplateGroup: (id) => {
    return defHttp.delete({
      url: `/aiad/diy/templates/groups/${id}`,
    });
  },

  // 给DIY模板设置分组
  setDIYTemplateGroup: (params) => {
    return defHttp.put({
      url: `/aiad/diy/templates/set-groups`,
      params,
    });
  },

  // 获取Temu模板分组
  getTemuTemplateGroups: (params) => {
    return defHttp.get({
      url: '/aiad/templates/goods/groups',
      params,
    });
  },

  // 新增Temu模板分组
  addTemuTemplateGroup: (params) => {
    return defHttp.post({
      url: '/aiad/templates/goods/groups',
      params,
    });
  },

  // 通过skuid查询关联的DIY模版画布
  getDIYTemplateBySkuId: (skuId: string) => {
    return defHttp.get({
      url: `/aiad/diy/templates/skus/${skuId}`,
    });
  },

  // 更新skuid关联DIY模版画布
  updateDIYTemplateSkuId: (params) => {
    return defHttp.put({
      url: `/aiad/diy/templates/skus`,
      params,
    });
  },

  // 修改Temu模板分组
  updateTemuTemplateGroup: (id, params) => {
    return defHttp.put({
      url: `/aiad/templates/goods/groups/${id}`,
      params,
    });
  },

  // 删除Temu模板分组
  deleteTemuTemplateGroup: (id) => {
    return defHttp.delete({
      url: `/aiad/templates/goods/groups/${id}`,
    });
  },

  // 给mTemu模板设置分组
  setTemuTemplateGroup: (params) => {
    return defHttp.put({
      url: `/aiad/templates/goods/set-groups`,
      params,
    });
  },

  // 给mockup模板设置分组
  setGroupTemplateGroup: (params) => {
    return defHttp.put({
      url: `/aiad/mock/templates/set-groups`,
      params,
    });
  },

  addWorkflowTask: (params: { images: any[]; sourceBatchId?: string; configId: boolean }) => {
    return defHttp.post({
      url: `/aiad/batch/workflow/creations`,
      params,
    });
  },

  addVectorTask: (params: {
    urls?: string[];
    images: any[];
    sourceBatchId?: string;
    extendTag: boolean;
  }) => {
    return defHttp.post({
      url: `/aiad/batch/vector`,
      params,
    });
  },
  addRemoveTask: (params: { urls: string[] }) => {
    return defHttp.post({
      url: `/aiad/batch/remove`,
      params,
    });
  },
  addColorTask: (params: { urls: string[]; colors: string[] }) => {
    return defHttp.post({
      url: `/aiad/batch/colors`,
      params,
    });
  },
  addTitleTask: (params: {
    prompt?: string;
    urls?: string[];
    images: any[];
    extendTag: boolean;
    sourceBatchId?: string;
  }) => {
    return defHttp.post({
      url: `/aiad/batch/title`,
      params,
    });
  },

  addUpscaleTask: (params) => {
    return defHttp.post({
      url: `/aiad/batch/upscale`,
      params,
    });
  },

  addUpscaleProduction: (params) => {
    return defHttp.post({
      url: `/aiad/batch/production`,
      params,
    });
  },

  addImageProcessingTask: (params) => {
    return defHttp.post({
      url: `/aiad/batch/image-processing`,
      params,
    });
  },

  addBatchImageProcessingTask: (params) => {
    return defHttp.post({
      url: `/aiad/batch/batch-image-processing`,
      params,
    });
  },

  addGroupPhotoTask: (params) => {
    return defHttp.post({
      url: `/aiad/batch/group-photo`,
      params,
    });
  },

  addImageEditingTask: (params) => {
    return defHttp.post({
      url: `/aiad/batch/editor`,
      params,
    });
  },

  addImageExpandTask: (params) => {
    return defHttp.post({
      url: `/aiad/batch/expand`,
      params,
    });
  },

  addWatermarkTask: (params) => {
    return defHttp.post({
      url: `/aiad/batch/watermark`,
      params,
    });
  },

  addImageTilingTask: (params) => {
    return defHttp.post({
      url: `/aiad/batch/tiling`,
      params,
    });
  },

  getCrawls: (params: {
    ability: string;
    endTime?: string;
    page: number;
    pageSize: number;
    sortField?: string;
    sortOrder?: string;
    startTime?: string;
    status?: string;
    batchNo?: string;
    userId?: string;
  }) => {
    return defHttp.get({
      url: `/aiad/batch/crawls`,
      params,
    });
  },

  addRedrawTask: (params: any) => {
    return defHttp.post({
      url: `/aiad/batch/redraw`,
      params,
    });
  },

  addVideoTask: (params: any) => {
    return defHttp.post({
      url: `/aiad/batch/video`,
      params,
    });
  },

  addPetDressupTask: (params) => {
    return defHttp.post({
      url: `/aiad/batch/pet-dressup`,
      params,
    });
  },

  addPartReplaceTask: (params: {
    extendTag?: boolean;
    num: number;
    images?: {
      afters?: string[];
      before?: string;
      sourceId?: string;
      url?: string;
    }[];
    sourceBatchId?: string;
    type: string;
    batch: boolean;
  }) => {
    return defHttp.post({
      url: `/aiad/batch/part-replace`,
      params,
    });
  },

  addCrawlTask: (params: {
    category?: string;
    height?: Record<string, unknown>;
    keyword?: string;
    low?: Record<string, unknown>;
    num?: number;
    platform?: string;
    rate?: boolean;
    sort?: string;
    type?: string;
    urls?: Record<string, unknown>[];
  }) => {
    return defHttp.post({
      url: `/aiad/batch/crawls`,
      params,
    });
  },

  getCrawDetail: (id) => {
    return defHttp.get({
      url: `/aiad/batch/crawls/${id}`,
    });
  },

  getCrawlInfo: () => {
    return defHttp.get<{
      hideNum: Nullable<number>;
      recharge: boolean;
      price: number;
      limit: number;
    }>({
      url: `/aiad/batch/crawls/info`,
    });
  },

  confirmCrawlInfo: (params: { notRemind: boolean }) => {
    return defHttp.post({
      url: `/aiad/batch/crawls/confirm`,
      params,
    });
  },

  getCrawDetailList: (params: {
    id: string | number;
    page: number;
    pageSize: number;
    sortField?: string;
    sortOrder?: string;
    status?: string | number;
    discard?: boolean;
  }) => {
    return defHttp.get({
      url: `/aiad/batch/crawls/${params.id}/jobs`,
      params,
    });
  },

  getCrawPluginList: (params: {
    page: number;
    pageSize: number;
    sortField?: string;
    sortOrder?: string;
    platform?: string;
    status?: string | number;
    startTime?: any;
    endTime?: any;
    userId?: string;
  }) => {
    return defHttp.get({
      url: `/aiad/batch/crawls/plugin/jobs`,
      params,
    });
  },

  //#region 我的空间
  // 查询文件，支持分页
  getFiles: (params: {
    ability: string;
    batchNo: string;
    startTime: string;
    endTime: string;
    page: number;
    pageSize: number;
    sortField: string;
    sortOrder: string;
    tags: number[];
  }) => {
    return defHttp.post<PageWrapper<IBatchFile>>({
      url: `/aiad/batch/space/files`,
      params,
    });
  },

  // 文件详情
  getFileDetail: (id) => {
    return defHttp.get<IBatchFile>({
      url: `/aiad/batch/space/files/${id}`,
    });
  },
  // 文件添加标签
  fileAddTags: (params: { ids: string[]; tags: string[]; type?: 0 | 1 }) => {
    return defHttp.post({
      url: `/aiad/batch/space/files/tags`,
      params,
    });
  },
  // 文件删除标签
  fileDeleteTags: (params: { ids; tags: string[] }) => {
    return defHttp.delete({
      url: `/aiad/batch/space/files/tags`,
      params,
    });
  },

  uploadFiles: (
    uploadFiles: { url: string; name?: string; md5?: string; type?: string }[],
    params = {}
  ) => {
    return defHttp.post({
      url: `/aiad/batch/space/files/upload`,
      params: {
        ...params,
        uploadFiles,
      },
    });
  },

  // 批量删除文件
  deleteFiles: (ids, deleted = false) => {
    const idChunks = chunk(ids, 20);
    // 串行执行删除
    return idChunks.reduce(
      (promise, chunk) =>
        promise.then(() =>
          defHttp.delete({
            url: `/aiad/batch/space/files`,
            params: {
              ids: chunk,
              deleted,
            },
          })
        ),
      Promise.resolve()
    );
  },
  //#endregion

  //#region 标签
  // 查询标签组
  getTagGroups: (params?: { ids?; name?; type? }) => {
    return defHttp.get<IBatchTagGroup[]>({
      url: `/aiad/batch/tag/groups`,
      params,
    });
  },
  // 创建标签组
  createTagGroup: (params: { name: string; type?: string }) => {
    return defHttp.post({
      url: `/aiad/batch/tag/groups`,
      params,
    });
  },
  // 更新标签组
  updateTagGroup: (id, params: { name: string }) => {
    return defHttp.put({
      url: `/aiad/batch/tag/groups/${id}`,
      params,
    });
  },
  // 删除标签组
  deleteTagGroup: (id) => {
    return defHttp.delete({
      url: `/aiad/batch/tag/groups/${id}`,
    });
  },
  // 查询标签
  getTags: (params?: { groupId?: number; ids?: string[]; name?: string; type?: string }) => {
    return defHttp.get<IBatchTag[]>({
      url: `/aiad/batch/tags`,
      params,
    });
  },
  // 创建标签
  createTag: (params?: { groupId?: number; name?: string; type?: string }) => {
    return defHttp.post({
      url: `/aiad/batch/tags`,
      params,
    });
  },
  // 修改标签
  updateTag: (id, params?: { name?: string }) => {
    return defHttp.put({
      url: `/aiad/batch/tags/${id}`,
      params,
    });
  },
  // 删除标签
  deleteTag: (id) => {
    return defHttp.delete({
      url: `/aiad/batch/tags/${id}`,
    });
  },
  // 标签添加到标签组
  addTagToGroup: (id, groupIds) => {
    return defHttp.post({
      url: `/aiad/batch/tags/${id}/groups`,
      params: {
        groups: groupIds,
      },
    });
  },
  // 标签从添标签组删除
  removeTagFromGroup: (id, groupIds) => {
    return defHttp.delete({
      url: `/aiad/batch/tags/${id}/groups`,
      params: {
        groups: groupIds,
      },
    });
  },

  // 判断文件是否有标签
  hasTags: (params: { ids: string[] }) => {
    return defHttp.post({
      url: `/aiad/batch/space/files/has-tags`,
      params,
    });
  },

  //#endregion

  // 预览导出数据
  previewExportData: (params) => {
    return defHttp.post({
      url: '/aiad/batch/export',
      params,
    });
  },
  previewExportDataPages: (params) => {
    return defHttp.post({
      url: '/aiad/batch/export/pages',
      params,
    });
  },

  // 导出数据
  exportData: (params) => {
    return defHttp.post({
      url: '/aiad/batch/exports',
      params,
    });
  },

  // 取消任务
  cancelJob: (id) => {
    return defHttp.delete({
      url: `/aiad/batch/${id}`,
    });
  },

  // #region 文生图提示词相关

  // 获取助手配置
  getModelPrompt: (model: string) => {
    return defHttp.get({
      url: `/aiad/pa/models/${model}`,
    });
  },

  // 执行提示词助手
  executePromptAssist: (params) => {
    return defHttp.post({
      url: `/aiad/pa/models`,
      params,
    });
  },

  // 执行提示词助手
  postPromptAssist: (params) => {
    return defHttp.post({
      url: '/aiad/pa/assist',
      params,
    });
  },

  // 识图转文
  describeImage: async ({ url }) => {
    const data = await defHttp.post({
      url: `/aiad/creations/caption`,
      params: {
        imageUrl: url,
      },
    });
    return data?.promptEn;
  },

  // 提示词模板列表
  getPromptTemplates: (params) => {
    return defHttp.get({
      url: '/aiad/pa/templates',
      params,
    });
  },

  // 保存提示词模板
  savePromptTemplate: (params) => {
    return defHttp.post({
      url: '/aiad/pa/templates',
      params,
    });
  },

  // 更新提示词模板
  updatePromptTemplate: (id, params) => {
    return defHttp.put({
      url: `/aiad/pa/templates/${id}`,
      params,
    });
  },

  // 删除提示词模板
  deletePromptTemplate: (id) => {
    return defHttp.delete({
      url: `/aiad/pa/templates/${id}`,
    });
  },

  // 官方预设
  getStandardPromptTemplates: (params) => {
    return defHttp.get({
      url: '/aiad/pa/templates/pre',
      params,
    });
  },

  // 根据提示词裂变
  fissionPrompt: (params) => {
    return defHttp.post({
      url: '/aiad/pa/rewrite',
      params,
    });
  },

  // 解析excel
  parseExcel: (file) => {
    return defHttp.post({
      url: '/aiad/batch/txt2img/excel',
      params: {
        file,
      },
    });
  },

  promptAssistant: (params) => {
    return defHttp.post({
      url: '/aiad/batch/text/assistant',
      params,
    });
  },

  getTemuExcelTemplates: async (params: {
    name?: string;
    page?: number;
    pageSize?: number;
    sortField?: string;
    sortOrder?: string;
    groupId?: string;
    type?: string;
    templateGroupId?: string;
  }) => {
    if (params.templateGroupId) {
      const res: any = await defHttp.get({
        url: `/aiad/batch/templates`,
        params: {
          group: params.templateGroupId === 'all' ? undefined : params.templateGroupId,
          type: 'goods',
        },
      });
      return {
        ...res,
        items: res.items?.map((item) => {
          return {
            ...item.meta,
            createTime: item.createTime,
            modifyTime: item.modifyTime,
            name: item.name,
            templateGroupId: item.group,
            isTemplate: true,
          };
        }),
      };
    }
    return defHttp.get({
      url: '/aiad/templates/goods',
      params,
    });
  },

  getTemuExcelTemplatesById: (id: string) => {
    return defHttp.get({
      url: `/aiad/templates/goods/${id}`,
    });
  },

  parseTemuExcel: (params: { file: string; name: string; type: string }) => {
    return defHttp.post({
      url: '/aiad/templates/goods/excel',
      params,
      timeout: 60 * 10000,
    });
  },

  saveTemuExcel: (params: any) => {
    return defHttp.post({
      url: '/aiad/templates/goods',
      params,
    });
  },

  putTemuExcel: (id: any, params: any) => {
    return defHttp.put({
      url: `/aiad/templates/goods/${id}`,
      params,
    });
  },

  deleteTemuExcel: (id: any) => {
    return defHttp.delete({
      url: `/aiad/templates/goods/${id}`,
    });
  },

  getGoodTemplateTypes: () => {
    return defHttp.get({
      url: `/aiad/templates/goods/types`,
    });
  },

  exportTemuTemplate: (params: {
    batchId?: string | number;
    columns: string[];
    ids?: string[];
    templateIds?: string[];
    type?: string;
    dataSource?: string;
    translate?: boolean;
  }) => {
    return defHttp.post({
      url: '/aiad/batch/export/jobs',
      params,
    });
  },

  getExportTemuJob: (params: {
    ability?: string;
    batchNo?: string;
    endTime?: string;
    jobStatus?: string;
    page: number;
    pageSize: number;
    sortField?: string;
    sortOrder?: string;
    startTime?: string;
  }) => {
    return defHttp.get({
      url: '/aiad/batch/export/jobs',
      params,
    });
  },

  getExportTemuJobDetail: (id: string) => {
    return defHttp.get({
      url: `/aiad/batch/export/jobs/${id}`,
    });
  },

  //#endregion

  // 删除采集数据
  deleteCrawlGoods: (id) => {
    return defHttp.delete({
      url: `/aiad/batch/crawl-goods/${id}`,
    });
  },

  // 批量删除采集数据
  deleteCrawlBatchGoods: (params: {
    goods: Array<{
      allDelete: boolean;
      fileIds: string[];
      id: string;
    }>;
    recycle: boolean;
  }) => {
    const goodsChunks = chunk(params.goods, 20);
    return goodsChunks.reduce(
      (promise, chunk) =>
        promise.then(() =>
          defHttp.delete({
            url: `/aiad/batch/crawl-goods/batch`,
            params: {
              recycle: params.recycle,
              goods: chunk,
            },
          })
        ),
      Promise.resolve()
    );
  },

  // 删除采集数据
  deleteCrawlingData: (
    params: Array<{
      allDelete: boolean;
      fileIds: string[];
      id: string;
    }>
  ) => {
    console.log(params);
    return defHttp.delete({
      url: `/aiad/batch/crawl-goods`,
      params,
    });
  },

  // 根据货号搜索
  searchCrawlGoods: (params: { sku?: string; spu?: string }) => {
    return defHttp.get({
      url: `/aiad/batch/export/jobs/sku/images`,
      params,
    });
  },

  // 根据货号搜索
  downloadCrawlGoodUrl: (params: { dataId?: string; sku?: string; spu?: string; url?: string }) => {
    return defHttp.post({
      url: `/aiad/batch/export/jobs/sku/download`,
      params,
    });
  },

  // 根据货号批量搜索
  downloadCrawlGoodUrls: (params: any) => {
    return defHttp.post({
      url: `/aiad/batch/export/jobs/sku/downloads`,
      params,
    });
  },

  // 增加备注
  remark: (id: string, params: { remark?: string; remarkColor?: string }) => {
    return defHttp.put({
      url: `/aiad/batch/${id}/remark`,
      params,
    });
  },

  getDials: () => {
    return defHttp.get({
      url: `/aiad/batch/dials`,
    });
  },
  get3DDials: () => {
    return defHttp.get({
      url: `/aiad/batch/dials-three`,
    });
  },

  getRedrawStyles: (params: any) => {
    return defHttp.get({
      url: `/aiad/creations/redraw/styles`,
      params,
    });
  },

  getPresets: (params?: { type: string }) => {
    return defHttp.get({
      url: `/aiad/reimage/presets`,
      params,
    });
  },

  postPresets: (params: { type: string; url: string }) => {
    return defHttp.post({
      url: `/aiad/reimage/presets`,
      params,
    });
  },
  deletePresets: (id: string) => {
    return defHttp.delete({
      url: `/aiad/reimage/presets/${id}`,
    });
  },

  addWorkflowTemplate: (params: any) => {
    return defHttp.post({
      url: `/aiad/batch/workflows`,
      params,
    });
  },

  getWorkflowTemplates: async (params) => {
    if (params.templateGroupId) {
      const res: any = await defHttp.get({
        url: `/aiad/batch/templates`,
        params: {
          group: params.templateGroupId,
          type: 'workflow',
        },
      });
      return {
        ...res,
        items: res.items?.map((item) => {
          return {
            ...item.meta,
            name: item.name,
            templateGroupId: item.group,
            isTemplate: true,
          };
        }),
      };
    }
    return defHttp.get({
      url: '/aiad/batch/workflows',
      params,
    });
  },
  getWorkflowIpoddyTemplates: async (params) => {
    const res: any = await defHttp.get({
      url: `/aiad/batch/templates`,
      params: {
        group: params.templateGroupId,
        type: 'workflow',
      },
    });
    return {
      ...res,
      items: res.items?.map((item) => {
        return {
          ...item.meta,
          name: item.name,
          templateGroupId: item.group,
          isTemplate: true,
        };
      }),
    };
  },

  getWorkflowDetail: (id: string) => {
    return defHttp.get({
      url: `/aiad/batch/workflow/creations/${id}`,
    });
  },

  // 完成审核
  approveWorkflow: (params: { batchId: string; workflowId: string }) => {
    return defHttp.post({
      url: '/aiad/batch/workflow/creations/approve',
      params,
    });
  },

  // 修改工作流模板
  updateWorkflowTemplate: (id, params) => {
    return defHttp.put({ url: `/aiad/batch/workflows/${id}`, params });
  },

  // 修改工作流模板名称
  updateWorkflowTemplateName: (id, params: { name: string }) => {
    return defHttp.post({ url: `/aiad/batch/workflows/${id}/name`, params });
  },

  // 获取工作流模板详情
  getWorkflowTemplateInfo: (id) => {
    return defHttp.get({
      url: `/aiad/batch/workflows/${id}`,
    });
  },
  // 删除工作流模板
  deleteWorkflowTemplate: (id) => {
    return defHttp.delete({
      url: `/aiad/batch/workflows/${id}`,
    });
  },

  // 获取工作流模板分组
  getWorkflowTemplateGroups: (params) => {
    return defHttp.get({
      url: '/aiad/batch/workflows/groups',
      params,
    });
  },

  // 新增工作流模板分组
  addWorkflowTemplateGroup: (params) => {
    return defHttp.post({
      url: '/aiad/batch/workflows/groups',
      params,
    });
  },

  // 修改工作流模板分组
  updateWorkflowTemplateGroup: (id, params) => {
    return defHttp.put({
      url: `/aiad/batch/workflows/groups/${id}`,
      params,
    });
  },

  // 删除工作流模板分组
  deleteWorkflowTemplateGroup: (id) => {
    return defHttp.delete({
      url: `/aiad/batch/workflows/groups/${id}`,
    });
  },

  // 给工作流模板设置分组
  setWorkflowTemplateGroup: (params) => {
    return defHttp.put({
      url: `/aiad/batch/workflows/set-groups`,
      params,
    });
  },

  // 导入模板
  importTemplateByIds: (ids) => {
    return defHttp.post({
      url: `/aiad/mock/templates/import`,
      params: {
        ids,
      },
    });
  },

  // 创建单个任务
  createJob: (params) => {
    return defHttp.post({
      url: '/aiad/batch/jobs',
      params,
    });
  },

  // 查询任务明细
  getJobDetail: (id, batchId) => {
    return defHttp.get({
      url: `/aiad/batch/${batchId}/jobs/${id}`,
    });
  },

  // 识图转文
  imageToCaption: (params) => {
    return defHttp.post({
      url: '/aiad/creations/caption',
      params,
    });
  },

  // 抹除孔洞
  removeHole: (url) => {
    return defHttp.post({
      url: `/aiad/creations/remove-hole?url=${url}`,
    });
  },

  // 通过md5查询文件是否存在
  getFileByMd5: (md5: string, name?: string) => {
    return defHttp.post({
      url: `/aiad/batch/space/files/md5`,
      params: {
        md5,
        name,
      },
    });
  },

  createFileFromSku: (params: {
    uploadFiles: {
      name?: string;
      title?: string;
      url?: string;
    }[];
  }) => {
    return defHttp.post({
      url: `/aiad/batch/space/files/sku`,
      params,
    });
  },
  // 保存为官方模板
  saveAsOfficialTemplate: (type, id) => {
    return defHttp.post({
      url: `/aiad/batch/templates/${type}/${id}`,
    });
  },
  // batch店铺鉴权回调
  authCallback: (type, params) => {
    return defHttp.post({
      url: `/aiad/authorize/${type}`,
      params,
    });
  },

  // 查询店铺
  getShops: (params: any) => {
    return defHttp.get({
      url: `/aiad/shops`,
      params,
    });
  },

  // 修改店铺
  updateShop: (id: any, params: any) => {
    return defHttp.put({
      url: `/aiad/shops/${id}`,
      params,
    });
  },

  // 解绑店铺
  unBindShop: (id: any) => {
    return defHttp.delete({
      url: `/aiad/shops/${id}/authorize`,
    });
  },

  // 获取shein授权链接
  getSheInAuthUrl: (params: { name: string; type: string }) => {
    return defHttp.post({
      url: `/aiad/authorize/shein/link`,
      params,
    });
  },

  // 修改店铺状态
  updateShopStatus: (id: any, status: any) => {
    return defHttp.put({
      url: `/aiad/shops/${id}/status/${status}`,
    });
  },

  // 获取平台
  getShopPlatforms: () => {
    return defHttp.get({
      url: `/aiad/shops/platforms`,
    });
  },

  // 查询上架模版列表
  getShopTemplates: (params: any) => {
    return defHttp.get({
      url: `/aiad/shop/templates`,
      params,
    });
  },

  // 获取商品模版
  getProductTemplate: (params: any) => {
    return defHttp.get({
      url: `/aiad/shop/templates/product`,
      params,
    });
  },

  // 获取Temu里的商品模版
  getTemuProducts: (params: any) => {
    return defHttp.get({
      url: `/aiad/temu/products`,
      params,
    });
  },

  // 获取商品模版
  getShopTemplatesById: (id: any) => {
    return defHttp.get({
      url: `/aiad/shop/templates/${id}`,
    });
  },

  saveShopTemplate: (id: any, params: any) => {
    return defHttp.put({
      url: `/aiad/shop/templates/${id}`,
      params,
    });
  },

  addShopTemplate: (params: any) => {
    return defHttp.post({
      url: `/aiad/shop/templates`,
      params,
    });
  },

  removeShopTemplate: (id: any) => {
    return defHttp.delete({
      url: `/aiad/shop/templates/${id}`,
    });
  },

  updateShopTemplateName: (
    id: any,
    params: {
      name: string;
    }
  ) => {
    return defHttp.put({
      url: `/aiad/shop/templates/${id}/name`,
      params,
    });
  },

  // 获取商品列表
  getGoods: (params: any) => {
    return defHttp.post({
      url: `/aiad/goods`,
      params,
    });
  },

  // 获取商品详情
  getGoodsDetail: (id: any) => {
    return defHttp.get({
      url: `/aiad/goods/${id}`,
    });
  },

  // 删除商品
  removeGoods: (params: any) => {
    return defHttp.delete({
      url: `/aiad/goods`,
      params,
    });
  },

  // 采集数据移动
  crawlingToGoods: (params: any) => {
    return defHttp.post({
      url: `/aiad/goods/crawling`,
      params,
    });
  },

  mockupToGoods: (params: any) => {
    return defHttp.post({
      url: `/aiad/goods/mockup`,
      params,
    });
  },

  getGoodsTasks: (params: any) => {
    return defHttp.post({
      url: `/aiad/publish/jobs/query`,
      params,
    });
  },

  // 商品添加标签
  goodsAddTags: (params: { ids: string[]; tags: string[]; type?: 0 | 1 }) => {
    return defHttp.post({
      url: `/aiad/goods/tags`,
      params,
    });
  },

  // 商品删除标签
  goodsRemoveTags: (params: { ids: string[]; tags: string[]; type?: 0 | 1 }) => {
    return defHttp.delete({
      url: `/aiad/goods/tags`,
      params,
    });
  },

  // 商品删除轮播图
  goodsRemoveImage: (id: string, imageId: string) => {
    return defHttp.delete({
      url: `/aiad/goods/${id}/images/${imageId}`,
    });
  },

  goodsPublishJobs: (params: any) => {
    return defHttp.post({
      url: `/aiad/publish/jobs`,
      params,
    });
  },

  getGoodsBatchDetailList: (params: any) => {
    return defHttp.get({
      url: `/aiad/publish/jobs/${params.id}/details`,
      params,
    });
  },

  getGoodsBatchDetail: (id) => {
    return defHttp.get({
      url: `/aiad/publish/jobs/${id}`,
    });
  },

  // 取消上架任务
  cancelShelfJob: (id) => {
    return defHttp.delete({
      url: `/aiad/publish/jobs/${id}`,
    });
  },

  // 更新模板分组权限设置
  updateTemplateGroupPermission: (params: any) => {
    return defHttp.put({
      url: `/aiad/templates/group-perms/details`,
      params,
    });
  },
  // 获取模板分组权限设置
  getTemplateGroupPermission: (params: any) => {
    return defHttp.get({
      url: `/aiad/templates/group-perms/details`,
      params,
    });
  },

  // 查询我的常用工具列表
  getMyTools: () => {
    return defHttp.get({
      url: `/aiad/batch/my-tools`,
    });
  },

  // 添加我的常用工具列表
  addMyTools: (params: any) => {
    return defHttp.put({
      url: `/aiad/batch/my-tools`,
      params,
    });
  },

  // 删除我的常用工具列表
  removeMyTools: (params: any) => {
    return defHttp.delete({
      url: `/aiad/batch/my-tools`,
      params,
    });
  },

  // 查询AI试衣模特
  getTryOnModel: (params: any) => {
    return defHttp.get({
      url: `/aiad/batch/tryon-model`,
      params,
    });
  },

  // 查询AI试衣背景
  getTryOnBgs: (params: any) => {
    return defHttp.get({
      url: `/aiad/batch/tryon-bgs`,
      params,
    });
  },

  // 查询AI试衣姿势
  getTryOnPose: () => {
    return defHttp.get({
      url: `/aiad/batch/tryon-pose`,
    });
  },

  createShop: (params: any) => {
    return defHttp.post({
      url: `/aiad/shops`,
      params,
    });
  },

  sizeSpecList: (params: { catId: string; shopId: string }) => {
    return defHttp.get({
      url: `/aiad/temu/size-templates`,
      params,
    });
  },

  temuModels: (params: { catId: string; shopId: string }) => {
    return defHttp.get({
      url: `/aiad/temu/models`,
      params,
    });
  },

  temuAccessories: (params: any) => {
    return defHttp.post({
      url: `/aiad/temu/accessories`,
      params,
    });
  },

  temuFreightTemplates: (params: { siteIds: string[]; shopId: string }) => {
    return defHttp.post({
      url: `/aiad/temu/freightTemplates`,
      params,
    });
  },

  temuWarehouses: (params: { siteIds: string[]; shopId: string }) => {
    return defHttp.post({
      url: `/aiad/temu/warehouses`,
      params,
    });
  },

  updateTemuVideo: (params: { shopId: string; url: string }) => {
    return defHttp.post({
      url: `/aiad/temu/video`,
      params,
    });
  },

  pollingTemuVideo: (params: { shopId: string; vid: string }) => {
    return defHttp.get({
      url: `/aiad/temu/video`,
      params,
    });
  },

  getSheInSites(params: { shopId: string }) {
    return defHttp.get({
      url: `/aiad/shein/sites`,
      params,
    });
  },

  getSheInWarehouses(params: { shopId: string }) {
    return defHttp.get({
      url: `/aiad/shein/warehouses`,
      params,
    });
  },

  // 抠图修边
  mattingTrimming: (params) => {
    return defHttp.post<{
      url: string;
    }>({ url: `/aiad/creations/matting-Trimming`, params });
  },

  // 获取erp授权地址
  getErpAuthorize(type: string, params?: any) {
    return defHttp.get({
      url: `/aiad/erp/authorize/${type}`,
      params,
    });
  },

  unBindErp(type: string) {
    return defHttp.delete({
      url: `/aiad/erp/authorize/${type}`,
    });
  },

  // 宠物换装官方精选着装
  getPetOfficialPetStyle(params?: any) {
    return defHttp.get<
      Array<{
        id: number;
        preview: string;
        previewExt: string;
        image: string;
        prompt: string;
      }>
    >({
      url: `/aiad/batch/pet-outfit`,
      params,
    });
  },

  // 添加自定义宠物换装
  addPetStyle(image?: string) {
    return defHttp.post({
      url: `/aiad/batch/pet-outfit-self`,
      params: {
        image,
      },
    });
  },

  // 删除自定义宠物换装
  deletePetStyle(id: number) {
    return defHttp.delete({
      url: `/aiad/batch/pet-outfit-self/${id}`,
    });
  },
  // 查询自定义宠物换装
  queryPetStyle() {
    return defHttp.get<
      Array<{
        id: number;
        image: string;
      }>
    >({
      url: `/aiad/batch/pet-outfit-self`,
    });
  },

  // 获取案例列表
  getTextCases(params) {
    return defHttp.get({
      url: `/aiad/batch/case-center/cases`,
      params,
    });
  },

  // 获取案例分类
  getTextCaseCategories() {
    return defHttp.get({
      url: '/dict/ops/cases-types',
    });
  },

  // 设为案例
  setTextCase(params: {
    ability?: string;
    image: string;
    width: number;
    height: number;
    prompt?: string;
    meta?: any;
    firstCategory?: string;
    type: string;
    status: string;
  }) {
    return defHttp.post({
      url: '/aiad/batch/case-center/cases',
      params,
    });
  },

  getCaseType: () => {
    return defHttp.get({
      url: '/aiad/batch/case-center/category',
    });
  },

  getDefaultCaseType: () => {
    return defHttp.get({
      url: '/aiad/batch/case-center/category/default',
    });
  },

  // 获取直播预告列表
  getLivePreviewList() {
    return defHttp.get({
      url: '/aiad/batch/announce',
    });
  },

  // 查询空间大小
  getSpaceSize(params) {
    return defHttp.get({
      url: '/aiad/batch/space/size',
      params,
    });
  },

  // 修改文件名称
  setFileName(id, name) {
    return defHttp.post({
      url: `/aiad/batch/space/${id}/name`,
      params: {
        name,
      },
    });
  },

  // 清空回收站
  clearupRecycle(params: { includeDisabled: boolean }) {
    return defHttp.delete({
      url: '/aiad/batch/space/recycle/clear',
      params,
    });
  },

  // 恢复内容
  restoreFiles(ids) {
    const idChunks = chunk(ids, 20);
    // 将恢复改为串行执行
    return idChunks.reduce(
      (promise, chunk) =>
        promise.then(() =>
          defHttp.post({
            url: '/aiad/batch/space/recycle/restore',
            params: { ids: chunk },
          })
        ),
      Promise.resolve()
    );
  },

  batchCreateShopOperator(params: { shopIds: string[]; userIds: string[] }) {
    return defHttp.put({
      url: '/aiad/shops/operators/batch',
      params,
    });
  },

  saveShopOperator(id: string, params: { shopId: string; userIds: string[] }) {
    return defHttp.put({
      url: `/aiad/shops/${id}/operators`,
      params,
    });
  },

  updateTitle(params: { goodsId: number; id: number; value: string }) {
    return defHttp.put({
      url: `/aiad/goods/titles`,
      params,
    });
  },

  searchQuestion(params: { question: string }) {
    return defHttp.post({
      url: `/la/knowledge-fragments/search`,
      params,
    });
  },

  podCaseEvent: (params) => {
    return defHttp.post({
      url: `/ops/pod/cases`,
      params,
    });
  },

  feedback: (params) => {
    return defHttp.post({
      url: `/aiad/batch/feedbacks`,
      params,
    });
  },

  report: (params) => {
    return defHttp.post<void>({
      url: '/aiad/podcase/report',
      params,
    });
  },

  // 添加我的POD自定义
  addMyPodCustom: (params: any) => {
    return defHttp.post({
      url: `/aiad/batch/self/${params.type}`,
      params,
    });
  },
  // 查询我的POD自定义
  getMyPodCustom: (type) => {
    return defHttp.get({
      url: `/aiad/batch/self/${type}`,
    });
  },
  // 删除我的POD自定义
  deleteMyPodCustom: (id: number) => {
    return defHttp.delete({
      url: `/aiad/batch/self/${id}`,
    });
  },

  // 修改我的POD自定义
  updateMyPodCustom: (params) => {
    return defHttp.put({
      url: `/aiad/batch/self/${params.type}/${params.id}`,
      params,
    });
  },

  // 查询POD搜索历史记录
  getPodSearchRecord: () => {
    return defHttp.get({
      url: `/aiad/pod/records`,
    });
  },

  // 删除POD搜索历史记录
  removePodSearchRecord: (params: { keyword: string }) => {
    return defHttp.delete({
      url: `/aiad/pod/records?keyword=${params.keyword}`,
    });
  },

  // 上报POD搜索历史记录
  createPodSearchRecord: (params: { detail?: string; keyword?: string; type?: string }) => {
    return defHttp.post({
      url: `/aiad/pod/records`,
      params,
    });
  },

  // 获取当前版本信息
  getCurrentVersion: () => {
    return defHttp.get({
      url: `/aiad/batch/version`,
    });
  },
});
